import { useTenant } from "@/providers/session";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const useDeleteBankConnection = () => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [deleteBankConnection, { data, loading, error }] = useMutation(
    DELETE_BANK_CONNECTION_MUTATION,
    {
      optimisticResponse: {
        deleteBankConnection: true,
      },
      update: (cache, { data: { deleteBankConnection } }, { variables }) => {
        if (deleteBankConnection) {
          const { bankConnectionId } = variables as any;

          cache.modify({
            fields: {
              bankConnections(existingBankConnections = []) {
                return existingBankConnections.filter(
                  (bankConnection: any) =>
                    bankConnection.bankConnectionId !== bankConnectionId,
                );
              },
            },
          });
        }
      },
    },
  );

  const handleDeleteBankConnection = async (
    bankConnectionId: string,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    toast.promise(
      deleteBankConnection({
        variables: {
          tenantId,
          bankConnectionId,
        },
      }),
      {
        loading: t("generic_loading"),
        success: () => {
          onSuccess();
          return t("generic_success");
        },
        error: () => {
          onError();
          return t("generic_error", { richColors: true });
        },
      },
    );
  };

  return {
    deleteBankConnection: handleDeleteBankConnection,
    data: data?.deleteBankConnection,
    loading,
    error,
  };
};

export const DELETE_BANK_CONNECTION_MUTATION = gql`
  mutation DeleteBankConnection(
    $tenantId: String!
    $bankConnectionId: String!
  ) {
    deleteBankConnection(
      tenantId: $tenantId
      bankConnectionId: $bankConnectionId
    )
  }
`;
